<template>
  <div>
    <div class="fixed inset-0 flex items-start justify-center w-full h-ful bg-cw-dark-alt/95 pt-30 lg:pt-20 heightMd:pt-10 z-100">
      <div class="flex flex-col items-center justify-center relative z-2" v-if="!showQuitModal">
        <div>
          <h2 class="text-xl heightMd:text-lg font-bold text-white">Game Menu</h2>
        </div>

        <div class="mt-[120px] heightMd:mt-12 heightSm:mt-10 space-y-12 heightMd:space-y-7 text-white font-bold *:!w-full">
          <BaseButton
            @click="resumeGame"
            class="flex items-center justify-center text-white heightMd:w-[180px] !w-[320px] text-lg heightMd:text-[16px] heightSm:text-sm px-20 py-4 heightMd:py-3 tracking-normal"
            backgroundColor="var(--color-cw-olive)"
            borderColor="var(--color-cw-olive-border-shadow)"
          >
            Resume
          </BaseButton>

          <BaseButton
            @click="goToGuide"
            class="flex items-center justify-center text-white heightMd:w-[180px] !w-[320px] text-lg heightMd:text-[16px] px-20 heightSm:text-sm py-5 heightMd:py-3 tracking-normal"
            backgroundColor="var(--color-cw-green)"
            borderColor="var(--color-cw-green-border-shadow)"
          >
            How to Play
          </BaseButton>

          <BaseButton
            @click="showQuitModal = true"
            class="flex items-center justify-center text-white heightMd:w-[180px] !w-[320px] heightMd:text-[16px] px-20 heightSm:text-sm py-5 heightMd:py-3 tracking-normal"
            backgroundColor="var(--color-cw-red-light)"
            borderColor="var(--color-cw-red-border-shadow)"
          >
            Quit this game
          </BaseButton>
        </div>
      </div>

      <Transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        v-else
      >
        <div v-if="showQuitModal" class="flex items-start justify-center relative z-2 bounce-in">
          <div class="relative min-w-150 max-w-max bg-cw-dark rounded-[13px] px-10 pt-10 pb-12 mt-[20vh]">
            <button @click="showQuitModal = false" class="absolute top-5 right-5">
              <CloseIcon class="text-white" />
            </button>
            <div class="mt-1">
              <div class="pt-3 heightMd:pt-2 w-max mx-auto">
                <h3 class="text-center text-white mb-7 heightMd:mb-5 font-bold tracking-normal heightMd:text-lg text-2xl">
                  Are you sure you want to
                  <br />
                  quit the game?
                </h3>
                <div class="flex justify-center items-center flex-col gap-3">
                  <BaseButton @click="resumeGame" backgroundColor="var(--color-cw-green)" borderColor="var(--color-cw-green-border-shadow)" class="font-bold text-lg heightMd:text-[16px] px-8 py-4">
                    Resume
                  </BaseButton>
                  <button @click="quitGame" class="font-bold heightMd:text-sm text-[16px] tracking-normal text-cw-red-light px-7 py-3">Quit game</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Backdrop click area (only the areas not covered by the modal content) -->
      <div @click="resumeGame" class="absolute inset-0 z-1">
        <!-- Close button -->
        <a href="" @click.prevent="resumeGame" class="absolute heightMd:top-6 heightMd:left-14 top-12 left-20 text-gray-400 hover:text-gray-600 transition-colors duration-200 cursor-pointer">
          <CloseIcon class="w-8 h-8 heightMd:w-5 heightMd:h-5 text-white" />
        </a>

        <div class="absolute heightMd:top-6 heightMd:left-14 top-12 right-20" v-if="!isKiddiesAccount">
          <BaseButton background-color="var(--color-cw-olive)" border-color="#323617" class="w-10 md:w-11 lg:w-12 h-10 md:h-9 lg:h-10 justify-center" @click.stop="handleSoundToggle">
            <SpeakerMuteIcon v-if="!isSoundEnabled" class="w-5 h-5 md:w-6 md:h-6" />
            <SpeakerIcon v-else class="w-5 h-5 md:w-6 md:h-6" />
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BaseButton from '@/components/base/BaseButton.vue'
import SpeakerIcon from '@/assets/svgs/icons/speaker-simple-high-fill.svg'
import SpeakerMuteIcon from '@/assets/svgs/icons/speaker-simple-x-fill.svg'

import CloseIcon from '@/assets/svgs/icons/close.svg'
import { useAppStore } from '@/stores/app'
import { storeToRefs } from 'pinia'
import useSocket from '@/composables/useSocket'
import { AppWindowEvent, GameEvent } from '@/types/Events'
import { useGameStore } from '@/stores/game'
import { ref } from 'vue'
import { useHelpers } from '@/composables/useHelpers'
import { useUserStore } from '@/stores/user'
const { isPaused, showLearning, isSoundEnabled } = storeToRefs(useAppStore())
const { isKiddiesAccount } = storeToRefs(useUserStore())
const { updateGameState } = useGameStore()
const { resetAppState, toggleSound } = useAppStore()
const { sleep, printEvent, sendParentWindowMessage } = useHelpers()
const socket = useSocket()
const showQuitModal = ref(false)

const resumeGame = () => {
  showQuitModal.value = false
  isPaused.value = false
}

const goToGuide = () => {
  showLearning.value = true
  resumeGame()
}

const quitGame = () => {
  leaveGame()
}

const handleSoundToggle = () => {
  toggleSound()
}

const leaveGame = async () => {
  try {
    await socket.sendEventAsync(GameEvent.LeaveGame)
    updateGameState(null)
    await sleep(200)
    sendParentWindowMessage(AppWindowEvent.ExitGame)
    resetAppState()
  } catch (error) {
    printEvent('Failed to leave game:', error, 'error')
  }
}
</script>

<style scoped></style>
